import React, { useMemo, ReactNode, useState } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { MailOpenIcon, PhoneIcon } from "@heroicons/react/solid";
import dateFormat from "dateformat";

import { Ticket } from "../../../../../shared/types/Ticket";
import { sxProps } from "./service-request-details-information.style";
import { DetailInformation } from "./components/DetailInformation";
import {
  generateEmailLink,
  generatePhoneLink,
  getFailureDetails,
} from "./util";
import { DetailInformationLink } from "./components/DetailInformationLink";
import { DetailInformationAppointment } from "./components/DetailInformationAppointment";
import { AdfRenderer } from "../../../../../shared/components/AdfRenderer";

import {
  EventAvailableOutlined as CalendarIconCheck,
  EventOutlined as CalendarIconHome,
  EventNoteOutlined as CalendarIconFavorite,
  EventRepeatOutlined as CalendarIconRefresh,
  FingerprintOutlined as TouchIdIcon,
  PlaceOutlined as MapsPinIcon,
  PersonOutlineOutlined as ReporterIcon,
  SensorDoorOutlined as DoorIcon,
} from "@mui/icons-material";

export type ServiceRequestDetailsInformationProps = {
  ticket: Ticket;
};

export const ServiceRequestDetailsInformation = ({
  ticket,
}: ServiceRequestDetailsInformationProps) => {
  const { t } = useTranslation();
  const [appointmentDate, setAppointmentDate] = useState<Date>(
    ticket?.bookingDate
  );
  const dateMask = "dd.mm.yyyy";
  const fullDateMask = "dd.mm.yyyy HH:MM";

  const failureDetails = useMemo(() => {
    const failureDetails = getFailureDetails(ticket, t);
    const details: ReactNode[] = failureDetails.map((fd) => {
      return (
        <DetailInformation
          key={fd.key}
          icon={fd.icon}
          information={fd.failureInformation}
          label={t(fd.labelKey)}
        />
      );
    });
    return details;
  }, [ticket]); // eslint-disable-line react-hooks/exhaustive-deps

  const estimatedDate = useMemo(() => {
    if (!ticket.estimatedDate) {
      return <></>;
    }
    return (
      <DetailInformation
        label={t("+serviceRequest.details.estimatedDate")}
        information={dateFormat(ticket.estimatedDate, dateMask)}
        icon={CalendarIconRefresh}
      />
    );
  }, [ticket]); // eslint-disable-line react-hooks/exhaustive-deps

  const bookingDate = useMemo(() => {
    if (!appointmentDate) {
      return;
    } else {
      return dateFormat(appointmentDate, fullDateMask);
    }
  }, [appointmentDate]);

  return (
    <Box sx={sxProps.detailInformationsBox}>
      <DetailInformation
        icon={CalendarIconFavorite}
        information={dateFormat(ticket.created, fullDateMask)}
        label={t("+serviceRequest.details.creationDate")}
      />
      <DetailInformationAppointment
        icon={CalendarIconHome}
        information={bookingDate}
        appointment={appointmentDate}
        setAppointment={setAppointmentDate}
        label={"appointment"}
      />
      <DetailInformation
        icon={CalendarIconCheck}
        information={dateFormat(ticket.dueDate, dateMask)}
        label={t("+serviceRequest.details.dueDate")}
        isError={ticket.overDue}
      />
      {estimatedDate}
      <DetailInformation
        icon={ReporterIcon}
        information={ticket.reporter?.displayName}
        label={t("+serviceRequest.details.reporter")}
      />
      {failureDetails}

      <Box sx={sxProps.detailInformationDescriptionBox}>
        <Typography variant="overline" sx={sxProps.detailInformationHeader}>
          {t("+serviceRequest.details.description")}
        </Typography>
      </Box>
      <Box>
        <AdfRenderer content={ticket.description} />
      </Box>
      <Box sx={sxProps.detailInformationCustomerBox}>
        <Typography variant="overline" sx={sxProps.detailInformationHeader}>
          {t("+serviceRequest.details.customerInfo")}
        </Typography>
        <Box sx={sxProps.detailInformationCustomerTitleBox}>
          <Typography variant="h1">{ticket.fullName}</Typography>
        </Box>
        <Box sx={sxProps.detailInformationCustomerButtonsBox}>
          <Stack>
            <DetailInformationLink
              link={generateEmailLink(ticket)}
              information={ticket.email}
              icon={MailOpenIcon}
            />
            <DetailInformationLink
              link={generatePhoneLink(ticket)}
              information={ticket.mobile}
              icon={PhoneIcon}
            />
          </Stack>
        </Box>
        
        <Box sx={sxProps.detailInformationCustomerDetailsBox}>
          <DetailInformation
            icon={TouchIdIcon}
            information={ticket.tenantId}
            label={t("+serviceRequest.details.customerId")}
          />
          <DetailInformation
            icon={MapsPinIcon}
            information={ticket.fullAddress}
            label={t("+serviceRequest.details.customerAddress")}
          />
          <DetailInformation
            icon={DoorIcon}
            information={ticket.apartmentNumber}
            label={t("+serviceRequest.details.apartmentNumber")}
          />
        </Box>
      </Box> 
    </Box>
  );
};
